import type { ReactNode } from "react";

import { SensitiveText, usePrivacyMasking } from "@/components/common/Sensitive";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type LinkedScopeSearchHintVariant = "browse" | "search" | "empty";

interface LinkedScopeSearchHintProps {
  variant?: LinkedScopeSearchHintVariant;
  /** Tighter padding for the global-search overlay. */
  compact?: boolean;
  scopeNames?: string;
  onExpand: () => void;
}

function ScopeNamesSuffix({ scopeNames }: { scopeNames?: string }) {
  const mask = usePrivacyMasking();
  if (!scopeNames) return null;
  return (
    <>
      {" ("}
      {mask ? <SensitiveText value={scopeNames} /> : scopeNames}
      {")"}
    </>
  );
}

function messageFor(
  variant: LinkedScopeSearchHintVariant,
  scopeNames?: string,
): ReactNode {
  if (variant === "empty") {
    return (
      <>
        No matches in your linked bookings
        <ScopeNamesSuffix scopeNames={scopeNames} />. Search all bookings to
        include other operators’ files.
      </>
    );
  }
  if (variant === "browse") {
    return (
      <>
        Showing only your bookings
        <ScopeNamesSuffix scopeNames={scopeNames} />.
      </>
    );
  }
  return (
    <>
      Search is limited to your linked bookings
      <ScopeNamesSuffix scopeNames={scopeNames} />.
    </>
  );
}

/** One-click expand for ops/admin whose linked-bookings preference is on. */
export function LinkedScopeSearchHint({
  variant = "search",
  compact = false,
  scopeNames,
  onExpand,
}: LinkedScopeSearchHintProps) {
  const expandLabel =
    variant === "browse" ? "Show all bookings" : "Search all bookings";

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground",
        compact && "rounded-lg bg-muted/40 px-3 py-2",
      )}
      role="status"
    >
      <span>{messageFor(variant, scopeNames)}</span>
      <Button
        type="button"
        variant={compact ? "secondary" : "ghost"}
        size="sm"
        className="h-6 shrink-0 px-2 text-xs"
        onClick={onExpand}
      >
        {expandLabel}
      </Button>
    </div>
  );
}
